import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Grid,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Event as EventIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useMeeting } from '../contexts/MeetingContext';
import AddMeeting from './AddMeeting';
import ViewMeeting from './ViewMeeting';

const MeetingList = () => {
  const { meetings, fetchMeetings, deleteMeeting, loading } = useMeeting();
  const [addMeetingOpen, setAddMeetingOpen] = useState(false);
  const [editMeetingOpen, setEditMeetingOpen] = useState(false);
  const [viewMeetingOpen, setViewMeetingOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMeetings();
  }, []);

  const loadMeetings = async () => {
    try {
      await fetchMeetings();
    } catch (error) {
      setError('Failed to load meetings');
    }
  };

  const handleAddMeeting = () => {
    setSelectedMeeting(null);
    setAddMeetingOpen(true);
  };

  const handleEditMeeting = (meeting) => {
    setSelectedMeeting(meeting);
    setEditMeetingOpen(true);
  };

  const handleViewMeeting = (meetingId) => {
    setSelectedMeetingId(meetingId);
    setViewMeetingOpen(true);
  };

  const handleDeleteMeeting = (meetingId) => {
    setSelectedMeetingId(meetingId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteMeeting(selectedMeetingId);
      setDeleteDialogOpen(false);
      setSelectedMeetingId(null);
      await loadMeetings();
    } catch (error) {
      setError('Failed to delete meeting');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'ongoing': return 'warning';
      case 'cancelled': return 'error';
      default: return 'primary';
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString() + ' ' + 
           new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Meetings
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddMeeting}
          size="large"
        >
          Create Meeting
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EventIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4">{meetings.length}</Typography>
                  <Typography color="text.secondary">Total Meetings</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 2, fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography variant="h4">
                    {meetings.filter(m => m.status === 'scheduled').length}
                  </Typography>
                  <Typography color="text.secondary">Upcoming</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AttachFileIcon sx={{ mr: 2, fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4">
                    {meetings.filter(m => m.status === 'completed').length}
                  </Typography>
                  <Typography color="text.secondary">Completed</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EventIcon sx={{ mr: 2, fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography variant="h4">
                    {meetings.reduce((sum, m) => sum + (m.participant_count || 0), 0)}
                  </Typography>
                  <Typography color="text.secondary">Total Participants</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Meetings Table */}
      {meetings.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <EventIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No meetings found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first meeting to get started
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddMeeting}>
                Create Meeting
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Meeting</TableCell>
                  <TableCell>Date & Time</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Organizer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Participants</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {meetings.map((meeting) => (
                  <TableRow key={meeting.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {meeting.title}
                        </Typography>
                        {meeting.description && (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {meeting.description.length > 50 
                              ? `${meeting.description.substring(0, 50)}...` 
                              : meeting.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(meeting.meeting_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {meeting.location || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {meeting.organizer_name || 'Unknown'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={meeting.status} 
                        color={getStatusColor(meeting.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {meeting.participant_count || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewMeeting(meeting.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Meeting">
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={() => handleEditMeeting(meeting)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Meeting">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteMeeting(meeting.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add/Edit Meeting Dialog */}
      <AddMeeting
        open={addMeetingOpen || editMeetingOpen}
        onClose={() => {
          setAddMeetingOpen(false);
          setEditMeetingOpen(false);
          setSelectedMeeting(null);
        }}
        meeting={selectedMeeting}
        onSubmit={async () => {
          await loadMeetings();
        }}
      />

      {/* View Meeting Dialog */}
      <ViewMeeting
        open={viewMeetingOpen}
        onClose={() => {
          setViewMeetingOpen(false);
          setSelectedMeetingId(null);
        }}
        meetingId={selectedMeetingId}
        onEdit={(meeting) => {
          setViewMeetingOpen(false);
          setSelectedMeeting(meeting);
          setEditMeetingOpen(true);
        }}
        onDelete={(meetingId) => {
          setViewMeetingOpen(false);
          handleDeleteMeeting(meetingId);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this meeting? This action cannot be undone and will also delete all associated attachments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MeetingList;
